import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Shield, Globe, Instagram, Linkedin, MessageCircle, Mail } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/lib/auth';
import { uploadMedia } from '@/lib/utils/media';
import type { User } from '@/types';
import { VerificationBadge } from './VerificationBadge';
import { SiSubstack } from 'react-icons/si';

interface ProfileHeaderProps {
  user: User;
  isOwnProfile?: boolean;
}

export function ProfileHeader({ user, isOwnProfile = false }: ProfileHeaderProps) {
  const { completeProfile } = useAuth();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const heroInputRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<'profile' | 'hero' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewUrls, setPreviewUrls] = useState({
    profile: user.profile_image,
    hero: user.cover_image
  });
  const [conciergeOpen, setConciergeOpen] = useState(false);
  const [connectingInstagram, setConnectingInstagram] = useState(false);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: 'profile' | 'hero') => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(type);
      setError(null);

      // Create local preview
      const previewUrl = URL.createObjectURL(file);
      setPreviewUrls(prev => ({
        ...prev,
        [type]: previewUrl
      }));

      // Create a copy of the file for mobile compatibility
      const fileCopy = new File([file], file.name, {
        type: file.type,
        lastModified: file.lastModified,
      });

      // Upload the image
      const imageUrl = await uploadMedia(fileCopy, user.id, 'profile');
      
      // Update the user profile
      await completeProfile(
        type === 'profile' 
          ? { profile_image: imageUrl }
          : { cover_image: imageUrl }
      );

      // Clean up preview URL
      URL.revokeObjectURL(previewUrl);
      
      // Update preview with final URL
      setPreviewUrls(prev => ({
        ...prev,
        [type]: imageUrl
      }));
    } catch (error) {
      console.error('Failed to upload image:', error);
      setError(error instanceof Error ? error.message : 'Failed to upload image. Please try again.');
      
      // Revert preview on error
      setPreviewUrls(prev => ({
        ...prev,
        [type]: type === 'profile' ? user.profile_image : user.cover_image
      }));
    } finally {
      setUploading(null);
      
      // Clear input
      if (type === 'profile' && fileInputRef.current) {
        fileInputRef.current.value = '';
      } else if (type === 'hero' && heroInputRef.current) {
        heroInputRef.current.value = '';
      }
    }
  };

  // Placeholder for Instagram connect logic
  const handleConnectInstagram = async () => {
    setConnectingInstagram(true);
    // TODO: Replace with real OAuth flow
    setTimeout(() => {
      alert('Instagram connect coming soon!');
      setConnectingInstagram(false);
    }, 1000);
  };

  // Clear error after 5 seconds
  React.useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <div className="relative bg-earth-900 rounded-xl shadow-lg px-6 py-8 flex flex-col md:flex-row md:items-center md:gap-8">
      {/* Profile Image (left, larger) */}
      <div className="flex-shrink-0 flex flex-col items-center md:items-start">
        <div className="relative group">
          <img
            src={previewUrls.profile || `https://api.dicebear.com/7.x/initials/svg?seed=${user.full_name}&backgroundColor=1d1918&textColor=e8e4dc`}
            alt={user.full_name}
            className="w-40 h-40 md:w-48 md:h-48 rounded-3xl border-4 border-earth-800 object-cover bg-earth-800 shadow-2xl"
          />
          {isOwnProfile && (
            <Button
              size="sm"
              variant="secondary"
              className="absolute -bottom-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading !== null}
            >
              <Camera className="h-4 w-4" />
            </Button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleImageUpload(e, 'profile')}
            disabled={uploading !== null}
          />
        </div>
      </div>
      {/* Center: Name, badge, bio */}
      <div className="flex-1 flex flex-col items-center md:items-start mt-6 md:mt-0">
        <div className="flex items-center gap-2 mb-2">
          <h1 className="text-4xl md:text-5xl font-display text-sand-100 font-bold">{user.full_name}</h1>
          {user.is_verified && <VerificationBadge status="approved" showTooltip={true} />}
        </div>
        {user.bio && <p className="text-sand-300 text-center md:text-left max-w-xl mb-2">{user.bio}</p>}
        {user.role === 'creator' && user.creator_profile && (
          <div className="flex items-center gap-2 mt-2">
            <span className="text-sand-300">{user.creator_profile.business_name}</span>
            {(
              <div className="flex items-center gap-1 bg-sand-400/10 px-2 py-1 rounded-full text-sm">
                         
              </div>
            )}
          </div>
        )}
      </div>
      {/* Right: Concierge Actions */}
      <div className="flex flex-col items-center md:items-end gap-4 mt-6 md:mt-0 md:ml-auto">
        <Button
          className="w-full md:w-auto text-lg px-8 py-4 rounded-2xl shadow-xl font-bold opacity-60 cursor-not-allowed"
          variant="primary"
          size="lg"
          disabled
          title="Concierge is coming soon!"
        >
          <MessageCircle className="h-5 w-5 md:h-6 md:w-6 mr-2 md:mr-3" />
          AI Concierge <span className="ml-1 md:ml-2 text-sand-400 text-sm md:text-base font-normal">(Coming Soon)</span>
        </Button>
        {/* Quick AI Concierge Actions */}
        <div className="flex flex-col gap-2 w-full md:w-auto">
          <Button variant="secondary" size="sm" className="w-full opacity-60 cursor-not-allowed" disabled title="Coming soon!">Booking Help (Coming Soon)</Button>
          <Button variant="secondary" size="sm" className="w-full opacity-60 cursor-not-allowed" disabled title="Coming soon!">Experience Suggestions (Coming Soon)</Button>
          <Button variant="secondary" size="sm" className="w-full opacity-60 cursor-not-allowed" disabled title="Coming soon!">Contact Support (Coming Soon)</Button>
        </div>
      </div>
      {/* Concierge Modal (placeholder) */}
      {/* Concierge Modal (placeholder) intentionally disabled until launch */}
      <AnimatePresence>
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-4 left-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg"
          >
            <p className="text-sm text-red-500">{error}</p>
          </motion.div>
        )}
      </AnimatePresence>
      {!isOwnProfile && (
        <div className="flex justify-center md:justify-start items-center gap-4 text-sand-400 mt-4">
          {user.substack && (
            <a
              href={user.substack}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-sand-300 transition-colors"
            >
              <SiSubstack className="h-5 w-5" />
            </a>
          )}
          {user.linkedin && (
            <a
              href={user.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-sand-300 transition-colors"
            >
              <Linkedin className="h-5 w-5" />
            </a>
          )}
          {user.other_social_links?.website && (
            <a
              href={user.other_social_links.website}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-sand-300 transition-colors"
            >
              <Globe className="h-5 w-5" />
            </a>
          )}
        </div>
      )}
    </div>
  );
}